import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  TextField,
  FormControlLabel,
  Typography,
  Link as MuiLink,
  InputAdornment,
  IconButton,
  Box,
} from "@mui/material";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import useValidation from "../../hooks/useValidation";
import "./Signin.css";
import Background from "../../components/Background";
import { styled } from "@mui/system";
import Path from "../../router/LocalRoutes";
import Topnav from "../../components/Topnav";
import Copyrighttext from "../../components/Copyrighttext";
import {
  loginAccount as loginWithV2,
  readOnboardingTokens,
} from "../../features/onboarding/api";
import { ONBOARDING_BASE_PATH } from "../../features/onboarding/steps/registry";
import {
  loadFlowState,
  startExistingAccountFlow,
} from "../../features/onboarding/state/persistence";
import { signupEntryPath } from "../../router/signupDeepLink";
import { t } from "../../locales/t";
import {
  clearAccountContract,
  selectContract,
  selectContractLoaded,
} from "../../features/capabilities/capabilitySlice";
import {
  CAPABILITIES,
  hasCapability,
} from "../../features/capabilities/contract";

const RoundedTextField = styled(TextField)({
  borderRadius: "20px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "20px",
    backgroundColor: "#F1F7FA", // Set the input background color
  },
  "& .MuiInputBase-input": {
    padding: "10px",
    color: "#2A4876", // Set the input value color to green
    "&::placeholder": {
      color: "#151516", // Set the placeholder text color to red
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent", // Optional: make the border transparent
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent", // Optional: ensure border stays transparent on hover
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent", // Optional: ensure border stays transparent when focused
  },
});

const StyledIconButton = styled(IconButton)({
  width: "40px",
  height: "40px",
  color: "#7BB9D5",

  borderRadius: "50%", // Make it circular
  "&:hover": {
    backgroundColor: "#E0E7E9", // Slightly darker on hover
  },
});

const SigninForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false); // State for the checkbox
  const [loginError, setLoginError] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [formValues, setFormValues] = useState({
    email: "",
    password: "",
  });
  const { errors, validateInputs, resetValidation } = useValidation({
    email: formValues.email,
    // loginPassword: existence-only — login must accept passwords that
    // predate the current complexity rules (see useValidation).
    loginPassword: formValues.password,
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues({
      ...formValues,
      [name]: name === "email" ? value.toLowerCase() : value,
    });
    if (errors) {
      resetValidation();
    }
  };

  const handleSignIn = async () => {
    if (!validateInputs()) {
      return;
    }
    const payload = {
      email: formValues?.email?.toLowerCase(),
      password: formValues?.password,
    };
    setLoggingIn(true);
    setLoginError(null);
    try {
      const response = await loginWithV2(payload);
      dispatch(clearAccountContract());
      if (response?.onboardingRequired) {
        // Only incomplete accounts resume onboarding. Registration already
        // captured DOB, so this works even without a saved local draft.
        startExistingAccountFlow({
          email: response.account?.email,
          emailVerified: !response.verificationRequired,
        });
        navigate(ONBOARDING_BASE_PATH, { replace: true });
        return;
      }

      navigate("/dashboard", { replace: true });
    } catch (error) {
      setLoginError(error?.message ?? "Unable to sign in");
    } finally {
      setLoggingIn(false);
    }
  };

  const disable = loggingIn || !(formValues.email && formValues.password);

  return (
    <Background>
      <Topnav />
      <Box className="selection-box login-container">
        <Typography
          className="text-primary"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: {
              xs: "18px",
              sm: "20px",
              md: "24px",
              lg: "28px",
              xl: "32px",
            },
            lineHeight: {
              xs: "28px",
              sm: "30px",
              md: "24px",
              lg: "38px",
              xl: "42px",
            },
          }}
        >
          Welcome to Echo!
        </Typography>
        <Typography
          variant="body"
          sx={{
            fontWeight: 400,
            fontSize: {
              xs: "12px",
              sm: "13px",
              md: "14px",
              lg: "16px",
            },
            color: "#152134",
          }}
        >
          We&apos;re excited to have you on board.
        </Typography>
        <Box sx={{ marginTop: "20px" }}>
          {location.state?.sessionExpired && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("onboarding.session.expired")}
            </Alert>
          )}
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
              },
              color: "#152134",
              textAlign: "left",
              marginBottom: "5px",
            }}
          >
            Your Email Address
          </Typography>
          <RoundedTextField
            name="email"
            type="email"
            autoComplete="email"
            variant="outlined"
            placeholder="Enter here"
            fullWidth
            value={formValues.email}
            error={errors.email}
            helperText={<span className="error-msg">{errors.email}</span>}
            onChange={handleChange}
            slotProps={{
              input: {
                "aria-label": "email input",
              },
            }}
          />
        </Box>
        <Box sx={{ marginTop: "10px", marginBottom: "10px" }}>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
              },
              color: "#152134",
              textAlign: "left",
              marginBottom: "5px",
            }}
          >
            Your Password
          </Typography>
          <RoundedTextField
            name="password"
            autoComplete="current-password"
            variant="outlined"
            placeholder="Enter here"
            fullWidth
            type={showPassword ? "text" : "password"}
            value={formValues.password}
            error={errors.loginPassword}
            helperText={
              <span className="error-msg">{errors.loginPassword}</span>
            }
            onChange={handleChange}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <StyledIconButton onClick={handleTogglePasswordVisibility}>
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </StyledIconButton>
                  </InputAdornment>
                ),
                "aria-label": "password input",
              },
            }}
          />
        </Box>
        <Box
          className={"rememberbox"}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "space-between",

            flexDirection: { xs: "column", sm: "row" }, // Ensures it stays in a single line
            flexWrap: "nowrap", // Prevents wrapping to a new line
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={isChecked}
                onChange={handleCheckboxChange}
                sx={{
                  margin: 0,
                  padding: 0,
                  "&.Mui-checked": {
                    color: "#7BB9D5",
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              />
            }
            label={
              <Typography
                style={{
                  fontSize: {
                    xs: "12px",
                    sm: "13px",
                    md: "14px",
                    lg: "16px",
                  },
                  padding: "2px",
                  color: "#152134",
                }}
              >
                Remember me
              </Typography>
            }
            disableTypography // Prevents default label styling
            sx={{
              margin: 0, // Removes default margins
              padding: 0, // Removes default padding
              display: "flex",
              alignItems: "center", // Aligns with other page content
            }}
          />

          <MuiLink
            style={{
              color: "#152134",
            }}
            href={Path.ForgotPassword}
            className="forgot-password"
            sx={{
              textAlign: "right",
              flex: 1,
              fontWeight: 500,
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
                lg: "16px",
              },
            }}
          >
            Forgot Password?
          </MuiLink>
        </Box>
        {loginError && (
          <Typography sx={{ color: "red", marginBottom: "15px" }}>
            {loginError}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="login-button"
          sx={{
            marginTop: "10px",
            height: {
              xs: "40px",
              sm: "45px",
              md: "45px",
              lg: "50px",
              xl: "60px",
            },
            borderRadius: "100px",
            textTransform: "capitalize",
          }}
          onClick={handleSignIn}
          style={{
            backgroundColor: !disable ? "#2A4876" : "#AAB6C8", // Change color based on input values
            cursor: disable ? "pointer" : "not-allowed", // Disable hover effect when fields are not filled
          }}
          disabled={disable} // Disable the button if any input is empty
        >
          Login
        </Button>
        <Typography
          sx={{
            fontSize: {
              xs: "12px",
              sm: "13px",
              md: "14px",
              lg: "16px",
            },
            color: "#152134",
          }}
          variant="body2"
          className="sign-up-text"
        >
          Don&apos;t have an account?{" "}
          <Link
            style={{ color: "#2A4876" }}
            className="font-bold no-underline"
            // Goals replace the old single-role signup choice (ECH-375).
            to={signupEntryPath(ONBOARDING_BASE_PATH)}
          >
            Sign Up
          </Link>
        </Typography>
      </Box>
      <Box mt={2} sx={{ display: { xs: "block", sm: "none" } }}>
        <Copyrighttext />
      </Box>
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Copyrighttext />
      </Box>
    </Background>
  );
};

const Signin = () => {
  const contract = useSelector(selectContract);
  const contractLoaded = useSelector(selectContractLoaded);
  // A denied or failed capability load returns here from ProtectedRoute.
  // Keep sign-in available instead of bouncing between login and Dashboard.
  if (
    localStorage.getItem("token") &&
    (!contractLoaded || hasCapability(contract, CAPABILITIES.platformAccess))
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  const tokens = readOnboardingTokens();
  const flow = loadFlowState();
  if (
    (tokens?.access?.token || tokens?.refresh?.token) &&
    flow.email &&
    !flow.completed
  ) {
    return <Navigate to={ONBOARDING_BASE_PATH} replace />;
  }

  return <SigninForm />;
};

export default Signin;
